// API服务 - 集成多个免费API

// GitHub API类型定义
export interface GitHubRepo {
  id: number;
  name: string;
  full_name: string;
  description: string;
  html_url: string;
  stargazers_count: number;
  language: string;
  forks_count: number;
  owner: {
    login: string;
    avatar_url: string;
  };
  updated_at: string;
}

// TMDB电影类型定义
export interface TMDBMovie {
  id: number;
  title: string;
  overview: string;
  poster_path: string;
  backdrop_path: string;
  release_date: string;
  vote_average: number;
  vote_count: number;
  genre_ids: number[];
  popularity: number;
}

// 新闻类型定义
export interface NewsItem {
  id: number;
  title: string;
  body: string;
  userId: number;
  tags?: string[];
  reactions?: number;
  views?: number;
}

// Hacker News类型定义
export interface HackerNewsItem {
  id: number;
  title: string;
  url?: string;
  score: number;
  by: string;
  time: number;
  descendants?: number;
  type: string;
}

// API基础配置
const API_CONFIG = {
  github: {
    baseUrl: 'https://api.github.com',
    headers: {
      'Accept': 'application/vnd.github.v3+json',
      'User-Agent': 'Dopamine-Web-App'
    }
  },
  tmdb: {
    baseUrl: 'https://api.themoviedb.org/3',
    // 使用公开的API密钥（生产环境应该使用环境变量）
    apiKey: '3fd2be6f0c70a2a598f084ddfb75487c', // 这是一个示例密钥
    imageBaseUrl: 'https://image.tmdb.org/t/p/w500'
  },
  jsonPlaceholder: {
    baseUrl: 'https://jsonplaceholder.typicode.com'
  },
  hackerNews: {
    baseUrl: 'https://hacker-news.firebaseio.com/v0'
  }
};

// 通用请求函数
async function fetchWithTimeout(url: string, options: RequestInit = {}, timeout = 10000): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}

// GitHub API服务
export class GitHubService {
  static async getTrendingRepos(language = ''): Promise<GitHubRepo[]> {
    try {
      const queryParts = ['stars:>1000'];
      if (language) {
        queryParts.push(`language:${language}`);
      }
      const params = new URLSearchParams({
        q: queryParts.join(' '),
        sort: 'stars',
        order: 'desc',
        per_page: '20',
      });

      const url = `${API_CONFIG.github.baseUrl}/search/repositories?${params.toString()}`;
      
      const response = await fetchWithTimeout(url, {
        headers: API_CONFIG.github.headers
      });
      
      if (!response.ok) {
        throw new Error(`GitHub API error: ${response.status}`);
      }
      
      const data = await response.json();
      return data.items || [];
    } catch (error) {
      console.error('Failed to fetch GitHub trending repos:', error);
      return [];
    }
  }

  static async getReposByTopic(topic: string): Promise<GitHubRepo[]> {
    try {
      const url = `${API_CONFIG.github.baseUrl}/search/repositories?q=topic:${topic}&sort=stars&order=desc&per_page=10`;
      
      const response = await fetchWithTimeout(url, {
        headers: API_CONFIG.github.headers
      });
      
      if (!response.ok) {
        throw new Error(`GitHub API error: ${response.status}`);
      }
      
      const data = await response.json();
      return data.items || [];
    } catch (error) {
      console.error('Failed to fetch GitHub repos by topic:', error);
      return [];
    }
  }
}

// TMDB API服务
export class TMDBService {
  static async getPopularMovies(): Promise<TMDBMovie[]> {
    try {
      const url = `${API_CONFIG.tmdb.baseUrl}/movie/popular?api_key=${API_CONFIG.tmdb.apiKey}&language=zh-CN&page=1`;
      
      const response = await fetchWithTimeout(url);
      
      if (!response.ok) {
        throw new Error(`TMDB API error: ${response.status}`);
      }
      
      const data = await response.json();
      return data.results || [];
    } catch (error) {
      console.error('Failed to fetch popular movies:', error);
      return [];
    }
  }

  static async getTrendingMovies(): Promise<TMDBMovie[]> {
    try {
      const url = `${API_CONFIG.tmdb.baseUrl}/trending/movie/day?api_key=${API_CONFIG.tmdb.apiKey}&language=zh-CN`;
      
      const response = await fetchWithTimeout(url);
      
      if (!response.ok) {
        throw new Error(`TMDB API error: ${response.status}`);
      }
      
      const data = await response.json();
      return data.results || [];
    } catch (error) {
      console.error('Failed to fetch trending movies:', error);
      return [];
    }
  }

  static getImageUrl(path: string, size = 'w500'): string {
    if (!path) return '';
    return `https://image.tmdb.org/t/p/${size}${path}`;
  }
}

// JSONPlaceholder API服务（模拟新闻）
export class NewsService {
  static async getPosts(): Promise<NewsItem[]> {
    try {
      const url = `${API_CONFIG.jsonPlaceholder.baseUrl}/posts`;
      
      const response = await fetchWithTimeout(url);
      
      if (!response.ok) {
        throw new Error(`News API error: ${response.status}`);
      }
      
      const data = await response.json();
      return data.slice(0, 20); // 只取前20条
    } catch (error) {
      console.error('Failed to fetch news posts:', error);
      return [];
    }
  }

  static async getPostById(id: number): Promise<NewsItem | null> {
    try {
      const url = `${API_CONFIG.jsonPlaceholder.baseUrl}/posts/${id}`;
      
      const response = await fetchWithTimeout(url);
      
      if (!response.ok) {
        throw new Error(`News API error: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch news post:', error);
      return null;
    }
  }
}

// Hacker News API服务
export class HackerNewsService {
  static async getTopStories(): Promise<HackerNewsItem[]> {
    try {
      // 获取热门故事ID列表
      const topStoriesUrl = `${API_CONFIG.hackerNews.baseUrl}/topstories.json`;
      const response = await fetchWithTimeout(topStoriesUrl);
      
      if (!response.ok) {
        throw new Error(`Hacker News API error: ${response.status}`);
      }
      
      const storyIds = await response.json();
      const topIds = storyIds.slice(0, 20); // 只取前20个
      
      // 获取每个故事的详细信息
      const stories = await Promise.all(
        topIds.map(async (id: number) => {
          try {
            const storyUrl = `${API_CONFIG.hackerNews.baseUrl}/item/${id}.json`;
            const storyResponse = await fetchWithTimeout(storyUrl);
            
            if (storyResponse.ok) {
              return await storyResponse.json();
            }
            return null;
          } catch (error) {
            console.error(`Failed to fetch story ${id}:`, error);
            return null;
          }
        })
      );
      
      return stories.filter(story => story !== null);
    } catch (error) {
      console.error('Failed to fetch Hacker News top stories:', error);
      return [];
    }
  }
}

// 统一的API服务导出
export const ApiService = {
  github: GitHubService,
  tmdb: TMDBService,
  news: NewsService,
  hackerNews: HackerNewsService
};